import {
  ORCHESTRATION_SHELL_BOOTSTRAP_PATH,
  OrchestrationShellBootstrapSnapshot,
  type OrchestrationOptimizedShellStreamItem,
  type OrchestrationShellStreamItem,
} from "@afar-research/contracts";
import * as Data from "effect/Data";
import * as Effect from "effect/Effect";
import * as Option from "effect/Option";
import * as Ref from "effect/Ref";
import * as Stream from "effect/Stream";
import { HttpClient, HttpClientRequest, HttpClientResponse } from "effect/unstable/http";

import { environmentEndpointUrl } from "../environment/endpoint.ts";

const SHELL_BOOTSTRAP_HTTP_TIMEOUT = "10 seconds";

export class OptimizedShellBootstrapError extends Data.TaggedError("OptimizedShellBootstrapError")<{
  readonly reason:
    | "ticket-missing"
    | "http-failed"
    | "snapshot-watermark-mismatch"
    | "stream-invalid"
    | "ready-watermark-invalid";
  readonly message: string;
  readonly cause?: unknown;
}> {}

const fail = (reason: OptimizedShellBootstrapError["reason"], message: string, cause?: unknown) =>
  new OptimizedShellBootstrapError({ reason, message, cause });

type ReconciliationState =
  | { readonly phase: "awaiting-ticket" }
  | {
      readonly phase: "catch-up";
      readonly throughSequence: number;
      readonly ready: boolean;
    };

export const makeOptimizedHostViewStream = Effect.fn("ShellBootstrap.makeOptimizedHostViewStream")(
  function* <E>(input: {
    readonly httpBaseUrl: string;
    readonly stream: Stream.Stream<OrchestrationOptimizedShellStreamItem, E>;
  }) {
    const httpClient = yield* HttpClient.HttpClient;
    const endpoint = environmentEndpointUrl(input.httpBaseUrl, ORCHESTRATION_SHELL_BOOTSTRAP_PATH);
    const state = yield* Ref.make<ReconciliationState>({ phase: "awaiting-ticket" });

    return input.stream.pipe(
      Stream.mapEffect((item) =>
        Effect.gen(function* () {
          const current = yield* Ref.get(state);
          if (current.phase === "awaiting-ticket") {
            if (item.kind !== "ticket") {
              return yield* fail(
                "ticket-missing",
                "Optimized shell bootstrap did not begin with a ticket.",
              );
            }
            const response = yield* HttpClientRequest.get(endpoint).pipe(
              HttpClientRequest.setHeader("authorization", `Bearer ${item.ticket}`),
              httpClient.execute,
              Effect.flatMap(HttpClientResponse.filterStatusOk),
              Effect.timeout(SHELL_BOOTSTRAP_HTTP_TIMEOUT),
              Effect.mapError((cause) =>
                fail("http-failed", "Optimized shell bootstrap HTTP snapshot failed.", cause),
              ),
            );
            const bootstrap = yield* HttpClientResponse.schemaBodyJson(
              OrchestrationShellBootstrapSnapshot,
            )(response).pipe(
              Effect.mapError((cause) =>
                fail(
                  "http-failed",
                  "Optimized shell bootstrap returned an invalid snapshot.",
                  cause,
                ),
              ),
            );
            if (bootstrap.snapshot.snapshotSequence !== bootstrap.throughSequence) {
              return yield* fail(
                "snapshot-watermark-mismatch",
                "Optimized shell bootstrap snapshot and watermark disagree.",
              );
            }
            yield* Ref.set(state, {
              phase: "catch-up",
              throughSequence: bootstrap.throughSequence,
              ready: false,
            });
            return Option.some<OrchestrationShellStreamItem>({
              kind: "snapshot",
              snapshot: bootstrap.snapshot,
            });
          }

          if (item.kind === "ticket") {
            return yield* fail(
              "stream-invalid",
              "Optimized shell bootstrap issued more than one ticket.",
            );
          }
          if (item.kind === "ready") {
            if (current.ready || item.throughSequence < current.throughSequence) {
              return yield* fail(
                "ready-watermark-invalid",
                "Optimized shell bootstrap could not prove its ready watermark.",
              );
            }
            yield* Ref.set(state, {
              phase: "catch-up",
              throughSequence: item.throughSequence,
              ready: true,
            });
            return Option.some<OrchestrationShellStreamItem>({ kind: "ready" });
          }

          if (item.sequence <= current.throughSequence) {
            return Option.none<OrchestrationShellStreamItem>();
          }
          yield* Ref.set(state, {
            ...current,
            throughSequence: item.sequence,
          });
          return Option.some<OrchestrationShellStreamItem>(item);
        }),
      ),
      Stream.flatMap((item) => (Option.isSome(item) ? Stream.succeed(item.value) : Stream.empty)),
      Stream.mapError((cause) =>
        cause instanceof OptimizedShellBootstrapError
          ? cause
          : fail("stream-invalid", "Optimized shell bootstrap catch-up failed.", cause),
      ),
    );
  },
);
